// SAB-1054: the propose flow, rebuilt for a phone on Sleeper's shape.
//
// WHAT WAS WRONG. The desktop modal renders two side-by-side panels, each carrying a
// header, a "selected" tray AND a scrolling roster. On a 390px viewport that leaves each
// player roughly 150px minus a position badge and padding, so every name truncated to
// about five characters -- "Jordan ...", "MarSha...", "Kendri...". A trade UI where you
// cannot read who you are trading is not a small styling problem.
//
// WHAT CHANGED. The selection tray comes OUT of the columns and becomes a sticky bottom
// bar, which is the whole trick: the trays were consuming vertical space to show what the
// lists could have shown, and horizontal space to show it twice. With them gone each
// column is a full half-viewport of nothing but players, and a name fits.
//
// DESKTOP IS UNTOUCHED. This is a separate component chosen by useIsMobile() rather than
// responsive classes on the existing modal, because "desktop can stay" is a requirement
// and the safest way to honour it is to not edit it at all.
import { useState } from 'react';
import { Check, ArrowLeftRight, ChevronUp, X } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { shortPlayerName } from '@/lib/nflGameLine';

export interface TradeRosterPlayer {
  player_id: string;
  full_name: string;
  position: string;
  position_slot?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  myTeamName: string;
  otherTeams: { id: string; name: string }[];
  selectedTeamId: string;
  onSelectTeam: (id: string) => void;
  myRoster: TradeRosterPlayer[];
  theirRoster: TradeRosterPlayer[];
  mySelected: string[];
  theirSelected: string[];
  setMySelected: (fn: (s: string[]) => string[]) => void;
  setTheirSelected: (fn: (s: string[]) => string[]) => void;
  message: string;
  setMessage: (v: string) => void;
  submitting: boolean;
  onSubmit: () => void;
  positionColors: Record<string, string>;
}

/** One tappable player card. The whole card is the target, not a checkbox beside it. */
function PlayerCard({
  player, selected, onToggle, side, positionColors,
}: {
  player: TradeRosterPlayer; selected: boolean; onToggle: () => void;
  side: 'mine' | 'theirs'; positionColors: Record<string, string>;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid="trade-player-card"
      data-selected={selected ? 'true' : 'false'}
      className={cn(
        'w-full text-left rounded-lg border p-2 transition-colors min-w-0',
        selected
          // Sleeper's convention, kept: the side you GIVE fills, the side you RECEIVE is
          // outlined. Two selected states that look different is what stops a half-built
          // proposal reading as symmetric when it isn't.
          ? side === 'mine'
            ? 'bg-teal-500/20 border-teal-400/60'
            : 'bg-transparent border-primary ring-1 ring-primary/40'
          : 'bg-muted/20 border-border/60 hover:bg-muted/40',
      )}
    >
      <div className="flex items-start gap-1.5 min-w-0">
        <Badge variant="outline" className={cn('text-[10px] px-1 shrink-0', positionColors[player.position])}>
          {player.position}
        </Badge>
        <span className="flex-1 min-w-0" />
        {selected && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
      </div>
      {/* The name gets its OWN line rather than sharing one with the badge. That single
          change is what buys the characters back: on a shared line the badge and the check
          take ~60px of a ~165px column. shortPlayerName ("K. Gainwell") is the fallback
          only when the full name genuinely will not fit. */}
      <p className="mt-1 text-xs font-medium leading-tight break-words" title={player.full_name}>
        <span className="hidden min-[340px]:inline">{player.full_name}</span>
        <span className="min-[340px]:hidden">{shortPlayerName(player.full_name)}</span>
      </p>
    </button>
  );
}

export function NFLTradeProposeMobile({
  open, onOpenChange, myTeamName, otherTeams, selectedTeamId, onSelectTeam,
  myRoster, theirRoster, mySelected, theirSelected, setMySelected, setTheirSelected,
  message, setMessage, submitting, onSubmit, positionColors,
}: Props) {
  const [reviewing, setReviewing] = useState(false);
  const count = mySelected.length + theirSelected.length;
  const theirName = otherTeams.find(t => t.id === selectedTeamId)?.name ?? 'them';
  const nameOf = (pid: string, roster: TradeRosterPlayer[]) =>
    roster.find(r => r.player_id === pid)?.full_name ?? 'Unknown';

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) setReviewing(false); onOpenChange(v); }}>
      <SheetContent side="bottom" hideClose data-testid="trade-propose-mobile"
        className="p-0 flex flex-col gap-0 bg-background h-[100dvh]">
        <SheetTitle className="sr-only">Propose a trade</SheetTitle>
        <SheetDescription className="sr-only">
          Select players from each roster, then review and send the proposal.
        </SheetDescription>

        {/* pt-safe-top: full-height sheet, so in the wrapped app this row is the physical
            top of the screen and would otherwise sit under the status bar. */}
        <div className="pt-safe-top flex-none border-b border-border px-3 pb-2 pt-3 flex items-center gap-2">
          <ArrowLeftRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="font-display text-sm font-bold truncate min-w-0 flex-1">Propose Trade</p>
          <button type="button" aria-label="Close" data-testid="trade-propose-close"
            onClick={() => onOpenChange(false)}
            className="h-11 w-11 -mr-2 flex-none flex items-center justify-center rounded-lg text-muted-foreground active:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-none px-3 py-2 border-b border-border">
          <Select value={selectedTeamId} onValueChange={onSelectTeam}>
            <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Trade with…" /></SelectTrigger>
            <SelectContent>
              {otherTeams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {!selectedTeamId ? (
          <p className="px-4 py-8 text-center text-xs text-muted-foreground">Pick a team to trade with.</p>
        ) : (
          // TWO FULL-HEIGHT COLUMNS. Each scrolls independently so the rosters read side by
          // side rather than one pushing the other off-screen.
          <div className="flex-1 min-h-0 grid grid-cols-2 gap-2 px-2 py-2">
            {([['mine', myTeamName, myRoster, mySelected, setMySelected],
               ['theirs', theirName, theirRoster, theirSelected, setTheirSelected]] as const).map(
              ([side, label, roster, sel, setSel]) => (
                <div key={side} className="flex flex-col min-w-0 min-h-0">
                  <p className="flex-none px-1 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground truncate"
                     title={`${label} gives`}>
                    {label} gives
                  </p>
                  <div className="flex-1 min-h-0 overflow-y-auto space-y-1.5 pr-0.5">
                    {roster.length === 0
                      ? <p className="px-1 py-4 text-[11px] text-muted-foreground">No players.</p>
                      : roster.map(p => (
                          <PlayerCard
                            key={p.player_id} player={p} side={side}
                            positionColors={positionColors}
                            selected={sel.includes(p.player_id)}
                            onToggle={() => setSel(s =>
                              s.includes(p.player_id) ? s.filter(x => x !== p.player_id) : [...s, p.player_id])}
                          />
                        ))}
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* STICKY BOTTOM BAR. Replaces the two in-column trays -- this is what freed the
            width. pb-safe-bottom keeps it clear of the home indicator. */}
        {selectedTeamId && (
          <div className="flex-none border-t border-border bg-background px-3 py-2 pb-safe-bottom">
            <Button
              className="w-full h-11" disabled={count === 0}
              data-testid="trade-view-proposal"
              onClick={() => setReviewing(true)}
            >
              <ChevronUp className="mr-1.5 h-4 w-4" />
              Trade Proposal · {count} · View Proposal
            </Button>
          </div>
        )}

        {/* CONFIRM SHEET. Deliberately a second step: the columns show what you CAN pick,
            this shows what you HAVE picked, and mixing them is what made the original
            cramped. Sending is only reachable from here. */}
        <Sheet open={reviewing} onOpenChange={setReviewing}>
          <SheetContent side="bottom" data-testid="trade-review-sheet"
            className="max-h-[85dvh] overflow-y-auto pb-safe-bottom">
            <SheetTitle className="text-sm">Review proposal</SheetTitle>
            <SheetDescription className="sr-only">Both sides of the trade, an optional message, and send.</SheetDescription>
            <div className="mt-3 space-y-3">
              {([[`${myTeamName} sends`, mySelected, myRoster],
                 [`${theirName} sends`, theirSelected, theirRoster]] as const).map(([label, sel, roster]) => (
                <div key={label} className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground truncate">{label}</p>
                  {sel.length === 0
                    ? <p className="text-xs text-muted-foreground">Nothing selected</p>
                    : sel.map(pid => (
                        <p key={pid} className="text-sm truncate" title={nameOf(pid, roster)}>{nameOf(pid, roster)}</p>
                      ))}
                </div>
              ))}
              <Textarea value={message} onChange={e => setMessage(e.target.value)}
                placeholder="Add a message (optional)" className="text-sm" rows={2} />
              <Button
                className="w-full h-11" data-testid="trade-send"
                disabled={submitting || mySelected.length === 0 || theirSelected.length === 0}
                onClick={onSubmit}
              >
                {submitting ? 'Sending…' : 'Send Proposal'}
              </Button>
              {/* Stated, not disabled-and-silent: a dead Send with no reason is the defect
                  SAB-1045 removed from the player detail card. */}
              {(mySelected.length === 0 || theirSelected.length === 0) && (
                <p className="text-[11px] text-muted-foreground text-center">
                  Pick at least one player from each side.
                </p>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </SheetContent>
    </Sheet>
  );
}
